import { api, axiosClient } from '@/lib/api/client';
import type {
  CoContactFilters,
  CoContactListResponse,
  CoContactWithInteractions,
  UpdateCoContactPayload,
  CoContact,
} from '@/lib/api/types';

/**
 * Caso 01 — Leads CRM. The backend exposes /co-contacts under JwtAuthGuard
 * and resolves the caller's billing root via PlanProvisioningService.
 *
 * `list` returns paginated rows; `detail` adds the interaction timeline.
 * `exportCsv` streams text/csv directly; we return the Blob so the caller
 * can wire up a hidden <a download>.
 */
export const contactsApi = {
  list: (filters: CoContactFilters) =>
    api.get<CoContactListResponse>('/co-contacts', { params: filters }),

  detail: (id: number) =>
    api.get<CoContactWithInteractions>(`/co-contacts/${id}`),

  update: (id: number, payload: UpdateCoContactPayload) =>
    api.patch<CoContact>(`/co-contacts/${id}`, payload),

  remove: (id: number) => api.delete<{ ok: true }>(`/co-contacts/${id}`),

  /**
   * Hits /co-contacts/export with the active filters. The backend streams
   * CSV up to 50k rows and returns 403 over that — caller should narrow
   * the filters in that case.
   */
  exportCsv: async (filters: CoContactFilters): Promise<Blob> => {
    const res = await axiosClient.get<Blob>('/co-contacts/export', {
      params: filters,
      responseType: 'blob',
    });
    return res.data;
  },
};
